import { clsx } from "clsx"

import {
  SUNGAGE_ACH_ASSUMED_LABEL,
  SUNGAGE_ASSUMED_PAYOFF_NOTE,
  SUNGAGE_MAX_PAYMENT_CONDITION,
} from "./sungage-application-copy"
import { monthlyUsd } from "../../../BatteryOnlyProposal/setup/finance-product-chrome"
import { formatUsdExact } from "../../../BatteryOnlyProposal/view-helpers"

/** A Sungage payment as the gateway sends it for any kind (Battery BrightStart, Solar). */
export type SungagePaymentDetailsView = {
  dppProduct: string
  termYears: number
  rate: number
  apr: number
  initialMonthlyPayment: number
  continuingMonthlyPayment: number
  /** Monthly payment if the deferred portion is not paid; not unconditional. */
  maxMonthlyPayment: number
  deferredPaymentPortion: number
  deferredPeriodMonths: number
  /** Assumed payoff of the deferred portion; not an assured tax credit. */
  assumedPaydownAmount: number
  achAssumed: boolean
}

const percent = (value: number): string => `${value.toFixed(2)}%`

/** Sunrise Escalator: no deferred portion, so none of Classic DPP's deferral rows. */
const SUNGAGE_ESCALATOR_DPP = `Escalator`

export const sungagePaymentRows = (
  details: SungagePaymentDetailsView,
  extras: Array<{ label: string; value: string }> = [],
): Array<{ label: string; value: string; note?: string }> => [
  { label: `Term`, value: `${details.termYears} years` },
  { label: `Rate`, value: percent(details.rate) },
  { label: `APR`, value: percent(details.apr) },
  {
    label: `Initial monthly payment`,
    value: `${monthlyUsd.format(details.initialMonthlyPayment)}/mo`,
  },
  {
    label: `Continuing monthly payment`,
    value: `${monthlyUsd.format(details.continuingMonthlyPayment)}/mo`,
  },
  ...(details.dppProduct === SUNGAGE_ESCALATOR_DPP
    ? []
    : [
        {
          label: `Maximum monthly payment`,
          value: `${monthlyUsd.format(details.maxMonthlyPayment)}/mo`,
          note: SUNGAGE_MAX_PAYMENT_CONDITION,
        },
        {
          label: `Deferred amount`,
          value: formatUsdExact(details.deferredPaymentPortion),
        },
        {
          label: `Deferred period`,
          value: `${details.deferredPeriodMonths} months`,
        },
        {
          label: `Assumed payoff`,
          value: formatUsdExact(details.assumedPaydownAmount),
          note: SUNGAGE_ASSUMED_PAYOFF_NOTE,
        },
      ]),
  {
    label: SUNGAGE_ACH_ASSUMED_LABEL,
    value: details.achAssumed ? `Yes` : `No`,
  },
  ...extras,
]

/**
 * Sungage's payment assumptions as the server returned them. Nothing here
 * amortizes a loan or calls the deferred portion a tax credit.
 */
export const SungagePaymentDetailsTable = ({
  details,
  extras = [],
}: {
  details: SungagePaymentDetailsView
  extras?: Array<{ label: string; value: string }>
}): JSX.Element => {
  const rows = sungagePaymentRows(details, extras)

  return (
    <table
      data-testid="sungage-payment-details"
      className="w-full text-[length:var(--step--2)]"
    >
      <tbody>
        {rows.map((row, index) => (
          <tr
            key={row.label}
            className={clsx(index > 0 && `border-t border-bp-border/60`)}
          >
            <th
              scope="row"
              className="px-4 py-2.5 text-left font-medium text-bp-text-muted"
            >
              {row.label}
              {row.note ? (
                <span className="mt-0.5 block font-normal">{row.note}</span>
              ) : null}
            </th>
            <td className="px-4 py-2.5 text-right tabular-nums text-bp-text">
              {row.value}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  )
}
